import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MetricCard } from "@/components/Reports/MetricCard";
import { HelpTooltip } from "@/components/HelpTooltip";
import { Building2, Users, TrendingUp, Award, BarChart, ChevronLeft, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useState } from "react";

export default function DepartmentsReport({
  departmentMetrics = [],
  departmentHashtags = [],
  sentDistribution = [],
  receivedDistribution = [],
  validDepartments = [],
  summaryMetrics = {},
  filters = {}
}) {
  const [customDateRange, setCustomDateRange] = useState({
    start_date: filters.start_date || '',
    end_date: filters.end_date || ''
  });

  const handleBackToReports = () => {
    router.visit("/admin?tab=reports");
  };

  const handleApplyCustomDates = () => {
    if (customDateRange.start_date && customDateRange.end_date) {
      router.visit("/admin/reports/departments", {
        data: {
          range: 'custom',
          start_date: customDateRange.start_date,
          end_date: customDateRange.end_date,
          department: filters.department
        }
      });
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.range) params.append('range', filters.range);

      const url = `/admin/reports/departments/export?${params.toString()}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `departments_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al exportar:', error);
    }
  };

  const COLORS = [
    '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899',
    '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#f43f5e'
  ];

  const participationData = departmentMetrics.map(d => ({
    department: d.department,
    participation: d.participation_rate
  })).sort((a, b) => b.participation - a.participation);

  const pointsData = departmentMetrics.map(d => ({
    name: d.department,
    value: d.total_points
  })).sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToReports}
              className="text-purple-600 hover:bg-purple-50 -ml-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver a Reportes
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-bambu-black flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            Reporte por Departamento
            <HelpTooltip content={
              <div>
                <p className="font-semibold mb-1">¿Qué es este reporte?</p>
                <p className="mb-2">Compara performance entre departamentos, identifica equipos con alta/baja participación y analiza cultura de reconocimiento por área.</p>
                <p className="font-semibold mb-1">Verás:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Reconocimientos enviados/recibidos por departamento</li>
                  <li>Tasa de participación de cada equipo</li>
                  <li>Hashtags más usados por departamento</li>
                  <li>Comparación entre áreas</li>
                </ul>
              </div>
            } />
          </h1>
          <p className="text-gray-500 mt-1">Análisis de engagement por área organizacional</p>
        </div>
        <Button
          onClick={handleExport}
          variant="outline"
          className="border-blue-200 text-blue-600 hover:bg-blue-50"
        >
          <BarChart className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Rango de Fechas</label>
            <select
              value={filters.range || "last_30_days"}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  router.visit("/admin/reports/departments", { data: { range: 'custom', department: filters.department } });
                } else {
                  router.visit("/admin/reports/departments", { data: { range: e.target.value, department: filters.department } });
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="last_7_days">Últimos 7 días</option>
              <option value="last_30_days">Últimos 30 días</option>
              <option value="last_90_days">Últimos 90 días</option>
              <option value="current_month">Mes actual</option>
              <option value="current_year">Año actual</option>
              <option value="custom">Rango personalizado...</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Departamento</label>
            <select
              value={filters.department || ""}
              onChange={(e) => router.visit("/admin/reports/departments", { data: { range: filters.range, start_date: filters.start_date, end_date: filters.end_date, department: e.target.value || undefined } })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Todos los departamentos</option>
              {validDepartments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          {filters.department && (
            <div className="flex items-end">
              <Button
                onClick={() => router.visit("/admin/reports/departments", { data: { range: filters.range, start_date: filters.start_date, end_date: filters.end_date } })}
                variant="outline"
                className="w-full border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Limpiar Filtros
              </Button>
            </div>
          )}
        </div>

        {/* Rango de fechas personalizado */}
        {filters.range === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Fecha Inicio</label>
              <input
                type="date"
                value={customDateRange.start_date}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Fecha Fin</label>
              <input
                type="date"
                value={customDateRange.end_date}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleApplyCustomDates}
                disabled={!customDateRange.start_date || !customDateRange.end_date}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aplicar Fechas
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Reconocimientos"
          value={summaryMetrics.total_recognitions || 0}
          icon={Award}
          color="orange"
          helpText="Suma total de reconocimientos entre todos los departamentos"
        />
        <MetricCard
          title="Puntos Totales"
          value={summaryMetrics.total_points || 0}
          icon={Target}
          color="green"
          suffix=" pts"
          helpText="Suma de puntos compartidos en todos los departamentos"
        />
        <MetricCard
          title="Departamentos Activos"
          value={summaryMetrics.total_departments || 0}
          icon={Building2}
          color="blue"
          helpText="Número de departamentos con actividad de reconocimiento"
        />
        <MetricCard
          title="Promedio por Depto"
          value={summaryMetrics.avg_per_department || 0}
          icon={TrendingUp}
          color="purple"
          helpText="Reconocimientos promedio por departamento"
        />
      </div>

      {filters.department && departmentHashtags.length > 0 && (
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Valores más usados en {filters.department}
            </CardTitle>
            <p className="text-sm text-gray-500">Top hashtags del departamento seleccionado</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {departmentHashtags.map((item) => (
                <Badge key={item.hashtag} className="bg-blue-100 text-blue-700 text-sm px-4 py-2">
                  {item.hashtag} - {item.count} usos ({item.percentage}%)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Comparación entre Departamentos
            <HelpTooltip content="Tabla comparativa con métricas detalladas de cada departamento: reconocimientos enviados/recibidos, participación y puntos totales. Requiere mínimo 3 usuarios activos por departamento" />
          </CardTitle>
          <p className="text-sm text-gray-500">Métricas de engagement por área (mínimo 3 usuarios activos)</p>
        </CardHeader>
        <CardContent>
          {departmentMetrics.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No hay datos disponibles</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Departamento</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Enviados</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Recibidos</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Participación</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Puntos Totales</th>
                  </tr>
                </thead>
                <tbody>
                  {departmentMetrics.map((dept, index) => (
                    <tr key={dept.department} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                            index === 0 ? "bg-yellow-100 text-yellow-700" :
                            index === 1 ? "bg-gray-100 text-gray-600" :
                            index === 2 ? "bg-orange-100 text-orange-700" :
                            "bg-blue-50 text-blue-600"
                          )}>
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{dept.department}</div>
                            <div className="text-xs text-gray-500">{dept.total_users} usuarios</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="font-medium text-gray-700">{dept.sent_count}</div>
                        <div className="text-xs text-gray-500">{dept.sent_points} pts</div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="font-medium text-gray-700">{dept.received_count}</div>
                        <div className="text-xs text-gray-500">{dept.received_points} pts</div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="text-sm font-bold text-purple-600">{dept.participation_rate}%</div>
                          <div className="text-xs text-gray-500">{dept.active_users}/{dept.total_users}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="font-mono font-bold text-green-600">{dept.total_points}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Ratio de Participación (%)
              <HelpTooltip content="Muestra el porcentaje de usuarios activos en cada departamento. Útil para identificar equipos que necesitan impulso en el programa de reconocimientos" />
            </CardTitle>
            <p className="text-sm text-gray-500">Porcentaje de usuarios activos por departamento</p>
          </CardHeader>
          <CardContent>
            {participationData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                No hay datos para mostrar
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={participationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="department"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                  <Bar dataKey="participation" fill="#8b5cf6" radius={[8, 8, 0, 0]} name="Participación %" />
                </RechartsBarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Distribución de Puntos
              <HelpTooltip content="Muestra cómo se distribuyen los puntos entre departamentos. Ayuda a identificar concentración o equilibrio en la distribución de reconocimientos" />
            </CardTitle>
            <p className="text-sm text-gray-500">Puntos totales por departamento</p>
          </CardHeader>
          <CardContent>
            {pointsData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                No hay datos para mostrar
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pointsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pointsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(value) => [`${value} pts`, 'Puntos']}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Reconocimientos Enviados vs Recibidos
            <HelpTooltip content="Compara cuántos reconocimientos da vs. recibe cada departamento. Identifica equipos generosos o equipos que necesitan más visibilidad" />
          </CardTitle>
          <p className="text-sm text-gray-500">Balance de reconocimientos por departamento</p>
        </CardHeader>
        <CardContent>
          {departmentMetrics.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No hay datos disponibles</div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <RechartsBarChart
                data={departmentMetrics}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="department"
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="sent_count" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Enviados" />
                <Bar dataKey="received_count" fill="#10b981" radius={[8, 8, 0, 0]} name="Recibidos" />
              </RechartsBarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

DepartmentsReport.layout = page => <AppLayout children={page} />;
